/**
 * Theater Setup Modal — Preset templates, import/export, reset
 */

import { useState, useCallback, useRef } from 'react'
import {
  applyTheaterPreset,
  importTheaterConfig,
  exportTheaterConfig,
  resetTheater,
} from '../../services/api'

interface TheaterSetupModalProps {
  onClose: () => void
  onTheaterChange: () => void
}

interface PresetInfo {
  key: string
  name: string
  description: string
  icon: string
  color: string
}

const PRESETS: PresetInfo[] = [
  {
    key: 'indopacom',
    name: 'INDOPACOM',
    description: 'Pacific / Maritime — high naval ordnance, long supply lines',
    icon: '🌊',
    color: '#0ea5e9',
  },
  {
    key: 'eucom',
    name: 'EUCOM',
    description: 'NATO / Europe — balanced PGM stocks, multi-domain',
    icon: '🏰',
    color: '#8b5cf6',
  },
  {
    key: 'centcom',
    name: 'CENTCOM',
    description: 'Desert / MENA — high CAS demand, heavy expenditure',
    icon: '🏜️',
    color: '#f59e0b',
  },
]

type ModalStatus = 'idle' | 'loading' | 'success' | 'error'

export function TheaterSetupModal({ onClose, onTheaterChange }: TheaterSetupModalProps) {
  const [status, setStatus] = useState<ModalStatus>('idle')
  const [statusMsg, setStatusMsg] = useState('')
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const [previewChanges, setPreviewChanges] = useState<Array<{ munition_id: string; old_on_hand: number; new_on_hand: number }> | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const showStatus = useCallback((type: ModalStatus, msg: string) => {
    setStatus(type)
    setStatusMsg(msg)
    if (type === 'success') {
      setTimeout(() => { setStatus('idle'); setStatusMsg('') }, 3000)
    }
  }, [])

  const handlePresetClick = useCallback(async (presetKey: string) => {
    if (selectedPreset === presetKey) {
      // Already selected — apply it
      setStatus('loading')
      setStatusMsg('Applying preset...')
      const res = await applyTheaterPreset(presetKey)
      if (res.data) {
        showStatus('success', `${res.data.preset} applied — ${res.data.changes.ordnance_changes.length} munitions updated`)
        setSelectedPreset(null)
        setPreviewChanges(null)
        onTheaterChange()
      } else {
        showStatus('error', res.error || 'Failed to apply preset')
      }
    } else {
      setSelectedPreset(presetKey)
      setPreviewChanges(null)
      // Fetch what would change by applying — we show the preset description
      // For a real diff we'd need a preview endpoint, but we'll show the description
    }
  }, [selectedPreset, onTheaterChange, showStatus])

  const handleExport = useCallback(async () => {
    setStatus('loading')
    setStatusMsg('Exporting...')
    const res = await exportTheaterConfig()
    if (res.data) {
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sentinel-theater-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      showStatus('success', 'Configuration exported')
    } else {
      showStatus('error', res.error || 'Export failed')
    }
  }, [showStatus])

  const handleImport = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const config = JSON.parse(text)

      // Validate structure
      if (!config.ordnance_overrides && !config.asset_overrides && !config.ordnance && !config.assets) {
        showStatus('error', 'Invalid config format. Expected ordnance_overrides/asset_overrides or ordnance/assets keys.')
        return
      }

      // Support both export format and direct override format
      let importPayload: { ordnance_overrides: Record<string, number>; asset_overrides: Record<string, Record<string, unknown>> }
      if (config.ordnance_overrides) {
        importPayload = config
      } else {
        // Convert export format to import format
        const ordOverrides: Record<string, number> = {}
        for (const [id, val] of Object.entries(config.ordnance || {})) {
          ordOverrides[id] = (val as { on_hand: number }).on_hand
        }
        importPayload = { ordnance_overrides: ordOverrides, asset_overrides: {} }
      }

      setStatus('loading')
      setStatusMsg('Importing...')
      const res = await importTheaterConfig(importPayload)
      if (res.data) {
        showStatus('success', 'Configuration imported successfully')
        onTheaterChange()
      } else {
        showStatus('error', res.error || 'Import failed')
      }
    } catch {
      showStatus('error', 'Failed to parse JSON file')
    }

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [onTheaterChange, showStatus])

  const handleReset = useCallback(async () => {
    if (!confirmReset) {
      setConfirmReset(true)
      return
    }
    setStatus('loading')
    setStatusMsg('Resetting...')
    const res = await resetTheater()
    if (res.data) {
      showStatus('success', 'Theater reset to defaults')
      setConfirmReset(false)
      onTheaterChange()
    } else {
      showStatus('error', res.error || 'Reset failed')
    }
  }, [confirmReset, onTheaterChange, showStatus])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-[600px] max-h-[80vh] overflow-auto term-box bg-term-bg border-term-primary/40 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-term-border">
          <div className="flex items-center gap-2">
            <span className="text-term-primary font-bold text-sm">THEATER CONFIGURATION</span>
          </div>
          <button
            onClick={onClose}
            className="text-term-textDim hover:text-term-text text-sm px-2 py-0.5 border border-term-border rounded hover:border-term-text transition-colors"
          >
            ESC
          </button>
        </div>

        {/* Status */}
        {status !== 'idle' && (
          <div className={`mx-4 mt-3 p-2 rounded text-xs font-medium ${
            status === 'loading' ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400' :
            status === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' :
            'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}>
            {status === 'loading' && '⏳ '}{status === 'success' && '✓ '}{status === 'error' && '✗ '}
            {statusMsg}
          </div>
        )}

        {/* Preset Templates */}
        <div className="p-4">
          <div className="text-[10px] text-term-textDim font-medium uppercase tracking-wider mb-3">
            Preset Templates
          </div>
          <div className="grid grid-cols-3 gap-3">
            {PRESETS.map(preset => {
              const isSelected = selectedPreset === preset.key
              return (
                <button
                  key={preset.key}
                  onClick={() => handlePresetClick(preset.key)}
                  disabled={status === 'loading'}
                  className={`p-3 rounded border text-left transition-all ${
                    isSelected
                      ? 'border-current ring-1 ring-current bg-current/10'
                      : 'border-term-border/50 bg-term-bgDark/50 hover:bg-term-bgLight hover:border-term-border'
                  } disabled:opacity-40`}
                  style={isSelected ? { color: preset.color, borderColor: preset.color } : {}}
                >
                  <div className="text-lg mb-1">{preset.icon}</div>
                  <div className="text-xs font-bold" style={{ color: isSelected ? preset.color : undefined }}>
                    {preset.name}
                  </div>
                  <div className="text-[10px] text-term-textDim mt-1 leading-tight">
                    {preset.description}
                  </div>
                  {isSelected && (
                    <div className="mt-2 text-[10px] font-bold uppercase" style={{ color: preset.color }}>
                      Click again to apply →
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 px-4">
          <div className="flex-1 border-t border-term-border/30" />
          <span className="text-[10px] text-term-textDim uppercase tracking-wider">or</span>
          <div className="flex-1 border-t border-term-border/30" />
        </div>

        {/* Import / Export / Reset */}
        <div className="p-4">
          <div className="text-[10px] text-term-textDim font-medium uppercase tracking-wider mb-3">
            Import / Export
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleImport}
              disabled={status === 'loading'}
              className="flex-1 px-3 py-2 text-xs font-medium border border-term-border rounded bg-term-bgDark hover:bg-term-bgLight hover:border-term-primary/40 transition-colors disabled:opacity-40"
            >
              ↑ Import JSON
            </button>
            <button
              onClick={handleExport}
              disabled={status === 'loading'}
              className="flex-1 px-3 py-2 text-xs font-medium border border-term-border rounded bg-term-bgDark hover:bg-term-bgLight hover:border-term-primary/40 transition-colors disabled:opacity-40"
            >
              ↓ Export Current
            </button>
            <button
              onClick={handleReset}
              disabled={status === 'loading'}
              className={`flex-1 px-3 py-2 text-xs font-medium border rounded transition-colors disabled:opacity-40 ${
                confirmReset
                  ? 'border-red-500/60 bg-red-500/10 text-red-400 hover:bg-red-500/20'
                  : 'border-term-border bg-term-bgDark hover:bg-term-bgLight hover:border-amber-500/40 text-term-textDim'
              }`}
            >
              {confirmReset ? '⚠ Confirm Reset' : '↻ Reset Default'}
            </button>
          </div>
          {confirmReset && (
            <div className="mt-2 text-[10px] text-amber-400">
              This will discard all changes and reload seed data. Click again to confirm, or{' '}
              <button onClick={() => setConfirmReset(false)} className="underline hover:text-amber-300">cancel</button>.
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelected}
            className="hidden"
          />
        </div>

        {/* Footer hint */}
        <div className="px-4 pb-4 text-[10px] text-term-textDim">
          Changes persist in backend memory until container restart. Export to save configurations.
        </div>
      </div>
    </div>
  )
}
